const QR_ECC = {
  1: { data: 19, ecc: 7, align: [] },
  2: { data: 34, ecc: 10, align: [6, 18] },
  3: { data: 55, ecc: 15, align: [6, 22] },
  4: { data: 80, ecc: 20, align: [6, 26] },
};

const QR_FORMAT_ECC = { L: 1 };

const normalizeText = (value = "") => String(value || "").trim();

const getQrVersion = (byteLength) => {
  if (byteLength <= 17) return 1;
  if (byteLength <= 32) return 2;
  if (byteLength <= 53) return 3;
  return 4;
};

const appendBits = (bits, value, length) => {
  for (let i = length - 1; i >= 0; i -= 1) {
    bits.push((value >>> i) & 1);
  }
};

const bitsToBytes = (bits) => {
  const result = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) {
      byte = (byte << 1) | (bits[i + j] || 0);
    }
    result.push(byte);
  }
  return result;
};

const gfMultiply = (x, y) => {
  let z = 0;
  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
};

const reedSolomonGenerator = (degree) => {
  let result = [1];
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    const next = Array(result.length + 1).fill(0);
    const currentRoot = root;
    result.forEach((coefficient, index) => {
      next[index] ^= gfMultiply(coefficient, currentRoot);
      next[index + 1] ^= coefficient;
    });
    result = next;
    root = gfMultiply(root, 0x02);
  }
  return result;
};

const reedSolomonRemainder = (data, degree) => {
  const generator = reedSolomonGenerator(degree);
  const result = Array(degree).fill(0);

  data.forEach((byte) => {
    const factor = byte ^ result.shift();
    result.push(0);
    generator.forEach((coefficient, index) => {
      result[index] ^= gfMultiply(coefficient, factor);
    });
  });

  return result;
};

const setModule = (matrix, reserved, x, y, value, isReserved = true) => {
  if (x < 0 || y < 0 || y >= matrix.length || x >= matrix.length) return;
  matrix[y][x] = Boolean(value);
  if (isReserved) reserved[y][x] = true;
};

const drawFinder = (matrix, reserved, x, y) => {
  for (let dy = -1; dy <= 7; dy += 1) {
    for (let dx = -1; dx <= 7; dx += 1) {
      const xx = x + dx;
      const yy = y + dy;
      const isDark =
        dx >= 0 &&
        dx <= 6 &&
        dy >= 0 &&
        dy <= 6 &&
        (dx === 0 || dx === 6 || dy === 0 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4));
      setModule(matrix, reserved, xx, yy, isDark, true);
    }
  }
};

const drawAlignment = (matrix, reserved, x, y) => {
  for (let dy = -2; dy <= 2; dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      setModule(matrix, reserved, x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1, true);
    }
  }
};

const drawFunctionPatterns = (matrix, reserved, version) => {
  const size = matrix.length;
  drawFinder(matrix, reserved, 0, 0);
  drawFinder(matrix, reserved, size - 7, 0);
  drawFinder(matrix, reserved, 0, size - 7);

  for (let i = 0; i < size; i += 1) {
    if (!reserved[6][i]) setModule(matrix, reserved, i, 6, i % 2 === 0, true);
    if (!reserved[i][6]) setModule(matrix, reserved, 6, i, i % 2 === 0, true);
  }

  (QR_ECC[version].align || []).forEach((x) => {
    (QR_ECC[version].align || []).forEach((y) => {
      if (!reserved[y][x]) drawAlignment(matrix, reserved, x, y);
    });
  });

  setModule(matrix, reserved, 8, size - 8, true, true);
  for (let i = 0; i < 9; i += 1) {
    if (i !== 6) {
      setModule(matrix, reserved, 8, i, false, true);
      setModule(matrix, reserved, i, 8, false, true);
    }
  }
  for (let i = 0; i < 8; i += 1) {
    setModule(matrix, reserved, size - 1 - i, 8, false, true);
    setModule(matrix, reserved, 8, size - 1 - i, false, true);
  }
};

const getMask = (mask, x, y) => {
  const patterns = [
    (x + y) % 2 === 0,
    y % 2 === 0,
    x % 3 === 0,
    (x + y) % 3 === 0,
    (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0,
    ((x * y) % 2) + ((x * y) % 3) === 0,
    (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
    (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
  ];
  return patterns[mask];
};

const drawFormatBits = (matrix, reserved, mask) => {
  const size = matrix.length;
  const data = (QR_FORMAT_ECC.L << 3) | mask;
  let bits = data << 10;
  for (let i = 14; i >= 10; i -= 1) {
    if (((bits >>> i) & 1) !== 0) bits ^= 0x537 << (i - 10);
  }
  const format = ((data << 10) | bits) ^ 0x5412;
  const getBit = (i) => ((format >>> i) & 1) !== 0;

  for (let i = 0; i <= 5; i += 1) setModule(matrix, reserved, 8, i, getBit(i), true);
  setModule(matrix, reserved, 8, 7, getBit(6), true);
  setModule(matrix, reserved, 8, 8, getBit(7), true);
  setModule(matrix, reserved, 7, 8, getBit(8), true);
  for (let i = 9; i < 15; i += 1) setModule(matrix, reserved, 14 - i, 8, getBit(i), true);

  for (let i = 0; i < 8; i += 1) setModule(matrix, reserved, size - 1 - i, 8, getBit(i), true);
  for (let i = 8; i < 15; i += 1) setModule(matrix, reserved, 8, size - 15 + i, getBit(i), true);
};

const makeQrBytes = (text, version) => {
  const encoder = new TextEncoder();
  const bytes = [...encoder.encode(text)];
  const capacity = QR_ECC[version].data;
  const bits = [];

  appendBits(bits, 0x4, 4);
  appendBits(bits, bytes.length, 8);
  bytes.forEach((byte) => appendBits(bits, byte, 8));
  appendBits(bits, 0, Math.min(4, capacity * 8 - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);

  const data = bitsToBytes(bits);
  for (let pad = 0xec; data.length < capacity; pad ^= 0xfd) data.push(pad);

  return [...data, ...reedSolomonRemainder(data, QR_ECC[version].ecc)];
};

const drawQrData = (matrix, reserved, codewords, mask) => {
  const size = matrix.length;
  const bits = [];
  codewords.forEach((byte) => appendBits(bits, byte, 8));

  let bitIndex = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right -= 1;
    for (let vert = 0; vert < size; vert += 1) {
      const y = upward ? size - 1 - vert : vert;
      for (let j = 0; j < 2; j += 1) {
        const x = right - j;
        if (!reserved[y][x]) {
          const bit = (bits[bitIndex] || 0) === 1;
          matrix[y][x] = bit ^ getMask(mask, x, y);
          bitIndex += 1;
        }
      }
    }
    upward = !upward;
  }
};

const calculatePenalty = (matrix) => {
  const size = matrix.length;
  let penalty = 0;
  const countRuns = (line) => {
    let runColor = line[0];
    let runLength = 1;
    for (let i = 1; i < line.length; i += 1) {
      if (line[i] === runColor) runLength += 1;
      else {
        if (runLength >= 5) penalty += runLength - 2;
        runColor = line[i];
        runLength = 1;
      }
    }
    if (runLength >= 5) penalty += runLength - 2;
  };

  for (let y = 0; y < size; y += 1) countRuns(matrix[y]);
  for (let x = 0; x < size; x += 1) countRuns(matrix.map((row) => row[x]));

  for (let y = 0; y < size - 1; y += 1) {
    for (let x = 0; x < size - 1; x += 1) {
      const color = matrix[y][x];
      if (matrix[y][x + 1] === color && matrix[y + 1][x] === color && matrix[y + 1][x + 1] === color) penalty += 3;
    }
  }

  const dark = matrix.flat().filter(Boolean).length;
  penalty += Math.floor(Math.abs((dark * 20) / (size * size) - 10)) * 10;
  return penalty;
};

export const makeQrMatrix = (value = "") => {
  const text = normalizeText(value);
  const version = getQrVersion(new TextEncoder().encode(text).length);
  const size = 21 + (version - 1) * 4;
  const codewords = makeQrBytes(text, version);
  let best = null;

  for (let mask = 0; mask < 8; mask += 1) {
    const matrix = Array.from({ length: size }, () => Array(size).fill(false));
    const reserved = Array.from({ length: size }, () => Array(size).fill(false));
    drawFunctionPatterns(matrix, reserved, version);
    drawQrData(matrix, reserved, codewords, mask);
    drawFormatBits(matrix, reserved, mask);
    const penalty = calculatePenalty(matrix);
    if (!best || penalty < best.penalty) best = { matrix, penalty };
  }

  return best.matrix;
};

export const qrMatrixToSvg = (matrix, { cell = 6, margin = 4, dark = "#0f172a", light = "#ffffff" } = {}) => {
  const size = matrix.length + margin * 2;
  const rects = [];
  matrix.forEach((row, y) => {
    row.forEach((value, x) => {
      if (value) {
        rects.push(`<rect x="${x + margin}" y="${y + margin}" width="1" height="1" />`);
      }
    });
  });

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size * cell}" height="${size * cell}" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="${light}"/><g fill="${dark}">${rects.join("")}</g></svg>`;
};

const ITF_PATTERNS = {
  0: "nnwwn",
  1: "wnnnw",
  2: "nwnnw",
  3: "wwnnn",
  4: "nnwnw",
  5: "wnwnn",
  6: "nwwnn",
  7: "nnnww",
  8: "wnnwn",
  9: "nwnwn",
};

export const makeBarcodeSvg = (value = "", { height = 54, narrow = 2, wide = 5, color = "#0f172a" } = {}) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (!digits) digits = "0";
  if (digits.length % 2 !== 0) digits = `0${digits}`;

  const modules = ["n", "n", "n", "n"];
  for (let i = 0; i < digits.length; i += 2) {
    const bars = ITF_PATTERNS[digits[i]];
    const spaces = ITF_PATTERNS[digits[i + 1]];
    for (let j = 0; j < 5; j += 1) {
      modules.push(bars[j], spaces[j]);
    }
  }
  modules.push("w", "n", "n");

  let x = 0;
  const rects = [];
  modules.forEach((module, index) => {
    const width = module === "w" ? wide : narrow;
    if (index % 2 === 0) rects.push(`<rect x="${x}" y="0" width="${width}" height="${height}" />`);
    x += width;
  });

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${x}" height="${height}" viewBox="0 0 ${x} ${height}" shape-rendering="crispEdges"><rect width="${x}" height="${height}" fill="#fff"/><g fill="${color}">${rects.join("")}</g></svg>`;
};

export const svgToDataUrl = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
